import React, { useState } from 'react';
import { Tag, Check, X, AlertCircle } from 'lucide-react';

const PromoCodeInput = ({ originalAmount, onPromoApplied, onPromoRemoved, serviceCategory, serviceName }) => {
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const EXCLUDED_CATEGORIES = [
    'Collection Tour du Monde'  
  ];

  // Codes promo disponibles
  const promoCodes = {
    'FOHOP26': {
      type: 'percentage',
      value: 10, //%
      description: 'Réduction partenaire hôpital',
      reusable: true, // Code réutilisable à l'infini
      excludedCategories: EXCLUDED_CATEGORIES
    },
    'POUJ26': {
      type: 'percentage',
      value: 10, //%
      description: 'Réduction partenaire poujoula',
      reusable: true, // Code réutilisable à l'infini
      excludedCategories: EXCLUDED_CATEGORIES
    },
    //ajout d'autres codes ici
  };

  const isServiceEligible = () => {
    // Vérifier si la catégorie du service est exclue
    return !EXCLUDED_CATEGORIES.includes(serviceCategory);
  };

  const validatePromoCode = (code) => {
    const upperCode = code.toUpperCase().trim();
    
    if (!upperCode) {
      setError('Veuillez entrer un code promo');
      return null;
    }

    const promo = promoCodes[upperCode];
    
    if (!promo) {
      setError('Code promo invalide');
      return null;
    }

    // Vérifier si le service est éligible
    if (!isServiceEligible()) {
      setError('Ce code promo ne s\'applique pas à cette prestation');
      return null;
    }

    return { code: upperCode, ...promo };
  };

  const calculateDiscount = (promo) => {
    if (promo.type === 'percentage') {
      return Math.round(originalAmount * (promo.value / 100) * 100) / 100;
    } else if (promo.type === 'fixed') {
      return Math.min(promo.value, originalAmount);
    }
    return 0;
  };

  const handleApplyPromo = async () => {
    setError('');
    setLoading(true);

    // Simuler une vérification (vous pouvez ajouter une vérification backend ici)
    setTimeout(() => {
      const validatedPromo = validatePromoCode(promoCode);
      
      if (validatedPromo) {
        const discount = calculateDiscount(validatedPromo);
        const newAmount = Math.max(0, originalAmount - discount);
        
        setAppliedPromo({
          ...validatedPromo,
          discount,
          newAmount
        });
        
        // Notifier le parent du changement
        onPromoApplied({
          code: validatedPromo.code,
          discount,
          newAmount,
          description: validatedPromo.description
        });
        
        setPromoCode('');
      }
      
      setLoading(false);
    }, 500);
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setError('');
    onPromoRemoved();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyPromo();
    }
  };

  // ✨ Si le service n'est pas éligible, afficher un message informatif
  if (!isServiceEligible()) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
        <div className="flex gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">ℹ️ Codes promo non applicables</p>
            <p>
              Les codes promo ne sont pas valables sur les forfaits et prestations spa privatif.
              {serviceCategory === 'Collection Tour du Monde' && ' Cette collection bénéficie déjà d\'une réduction incluse.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!appliedPromo ? (
        // Formulaire d'entrée du code promo
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Tag className="w-4 h-4 inline mr-2" />
            Code promo (optionnel)
          </label>
          
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                placeholder="Entrer un code promotionnel"
                className={`w-full px-4 py-3 rounded-lg border ${
                  error 
                    ? 'border-red-300 focus:border-red-500' 
                    : 'border-gray-300 focus:border-purple-500'
                } focus:outline-none`}
                disabled={loading}
              />
              {error && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
            </div>
            
            <button
              onClick={handleApplyPromo}
              disabled={loading || !promoCode.trim()}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                loading || !promoCode.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {loading ? 'Vérification...' : 'Appliquer'}
            </button>
          </div>
          
          {error && (
            <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            💡 Codes promo valables uniquement sur les massages individuels
          </p>
        </div>
      ) : (
        // Code promo appliqué avec succès
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Check className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-900">
                  Code promo appliqué !
                </span>
              </div>
              
              <div className="space-y-1 text-sm ml-7">
                <p className="text-gray-700">
                  <span className="font-mono font-bold text-green-700">
                    {appliedPromo.code}
                  </span>
                  {' - '}{appliedPromo.description}
                </p>
                
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-gray-600">
                    Réduction : 
                    <span className="font-bold text-green-700 ml-1">
                      -{appliedPromo.discount}€
                      {appliedPromo.type === 'percentage' && ` (-${appliedPromo.value}%)`}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleRemovePromo}
              className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-100 transition-colors"
              title="Retirer le code promo"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodeInput;